import { FormEvent, useCallback, useEffect, useState } from 'react';

import { Stack } from '@mui/material';
import Alert from '@mui/material/Alert';
import Typography from '@mui/material/Typography';

import BSTNAnimation from '@/components/Animations/BSTNAnimation';
import ArrowRightIcon from '@/components/Icons/ArrowRightIcon';
import Meta from '@/components/Meta';
import TextField from '@/components/TextField';
import { ApprovalStatus } from '@/graphql';
import { useUserLoading } from '@/hooks/useUser';
import { useUpdateUserMutation } from '@/hooks/useUser/__generated__/mutations.generated';
import Onboarding from '@/pages/Onboarding';
import Header from '@/pages/Onboarding/Header';
import { AnimationBackground, Container, Wrapper } from '@/pages/Onboarding/styled';
import { OnboardingStep } from '@/pages/Onboarding/types';
import { enableSegment } from '@/utils/analytics/segment/singleton';
import reloadPage from '@/utils/device/reloadPage';

import { Form, SubmitButton } from '../styled';
import { Rejected } from './Rejected';
import { Reviewing } from './Reviewing';
import { Content } from './styled';
import { validateURL } from './utils';

function WaitingRoom() {
  const { user, loading: userLoading } = useUserLoading();
  const [updateUser] = useUpdateUserMutation();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();

      setLoading(true);

      const data = new FormData(e.target as HTMLFormElement);

      const companyName = data.get('company') as string;
      const profileUse = data.get('use') as string;
      const linkedinUrl = data.get('linkedin-url') as string;

      if (!companyName || !profileUse || !linkedinUrl) {
        setLoading(false);
        setError('Please enter all fields before continuing.');
        return;
      }

      if (!validateURL(linkedinUrl)) {
        setLoading(false);
        setError('Please enter a valid website URL.');
        return;
      }

      try {
        await updateUser({
          variables: {
            user: {
              company_name: data.get('company') as string,
              profile_use: data.get('use') as string,
              linkedin_url: data.get('linkedin-url') as string,
            },
          },
        });

        reloadPage();
      } catch (err) {
        setError(err.message);
        setLoading(false);
      }
    },
    [updateUser],
  );

  useEffect(() => {
    enableSegment();
  }, []);

  if (userLoading) return null;

  if (user.status === ApprovalStatus.PendingOrgApproval)
    return <Onboarding providedStep={OnboardingStep.RequestedToJoin} />;

  return (
    <Meta title="Baseten | Verify your account">
      <Container>
        <AnimationBackground>
          <BSTNAnimation />
        </AnimationBackground>
        <Wrapper>
          <Header />
          <Content direction="column" padding={4}>
            {user?.status === ApprovalStatus.Pending && <Reviewing />}

            {user?.status === ApprovalStatus.Rejected && <Rejected />}

            {user?.status === ApprovalStatus.Default && (
              <>
                <Stack gap={2}>
                  <Typography variant="h3">
                    We need more information to approve your account
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ lineHeight: '20px' }}>
                    To speed up your approval, please share a little more about yourself and how you
                    want to use Baseten. You’ll receive an email when your account is approved.
                  </Typography>
                </Stack>
                <Form onSubmit={handleSubmit} sx={{ width: '100%' }}>
                  <Stack direction="column" gap={2}>
                    <TextField
                      defaultValue={user?.companyName}
                      label="Your company’s name"
                      name="company"
                      placeholder="AI Startup Inc."
                      required
                      variant="outlined"
                      fullWidth
                    />

                    <TextField
                      label="LinkedIn or other professional profile"
                      name="linkedin-url"
                      defaultValue={user?.linkedinProfile}
                      variant="outlined"
                      type="url"
                      required
                      fullWidth
                      placeholder="https://www.linkedin.com/in/your-profile"
                    />

                    <TextField
                      label="How do you want to use Baseten?"
                      name="use"
                      defaultValue={user?.profileUse}
                      variant="outlined"
                      multiline
                      inputProps={{ maxLength: 255 }}
                      rows={5}
                      fullWidth
                    />

                    {error && <Alert severity="error">{error}</Alert>}
                    <Stack alignItems="end" width="100%" mt={1}>
                      <SubmitButton
                        data-cy="submit-btn"
                        endIcon={<ArrowRightIcon />}
                        loading={loading}
                      >
                        Submit
                      </SubmitButton>
                    </Stack>
                  </Stack>
                </Form>
              </>
            )}
          </Content>
        </Wrapper>
      </Container>
    </Meta>
  );
}

export default WaitingRoom;
